import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ListChecks, Plus, Loader2, Trash2, Edit2, Users, Zap } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { id: null, name: '', description: '' };

export const EmailListsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [autoList, setAutoList] = useState(null);
  const [autoForm, setAutoForm] = useState({ action: 'enroll_sequence', target_id: '', active: true });
  const [savingAuto, setSavingAuto] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get('/email/lists'),
        api.get('/email/campaigns').catch(() => ({ data: { campaigns: [] } })),
      ]);
      setLists(r.data?.lists || []);
      setCampaigns(c.data?.campaigns || (Array.isArray(c.data) ? c.data : []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const sequences = campaigns.filter((c) => c.type === 'sequence' && (c.steps || []).length > 0);
  const broadcasts = campaigns.filter((c) => c.type === 'broadcast' && c.subject && c.body_text);

  const openAuto = (l) => {
    const a = l.automation || {};
    setAutoForm({ action: a.action || 'enroll_sequence', target_id: a.target_id || '', active: a.active !== false });
    setAutoList(l);
  };

  const labelFor = (l) => {
    const a = l.automation;
    if (!a || !a.active || !a.target_id) return null;
    const target = campaigns.find((c) => c.id === a.target_id);
    const verb = a.action === 'send_campaign' ? 'Send' : 'Enroll →';
    return `${verb} ${target?.name || '…'}`;
  };

  const saveAuto = async () => {
    if (autoForm.target_id) {
      setSavingAuto(true);
      try {
        await api.put(`/email/lists/${autoList.id}/automation`, autoForm);
        toast.success('Automation saved — new subscribers will trigger it');
        setAutoList(null); await load();
      } catch (e) { toast.error(e.response?.data?.detail || 'Could not save automation'); }
      finally { setSavingAuto(false); }
    } else {
      toast.error('Pick a sequence or campaign');
    }
  };

  const clearAuto = async () => {
    setSavingAuto(true);
    try {
      await api.put(`/email/lists/${autoList.id}/automation`, { action: autoForm.action, target_id: '', active: false });
      toast.success('Automation removed');
      setAutoList(null); await load();
    } catch { toast.error('Could not remove automation'); }
    finally { setSavingAuto(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (form.id) await api.put(`/email/lists/${form.id}`, { name: form.name, description: form.description });
      else await api.post('/email/lists', { name: form.name, description: form.description });
      toast.success(form.id ? 'List updated' : 'List created');
      setEditing(null); setForm(EMPTY);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete list "${l.name}"? Subscribers are kept, just removed from this list.`)) return;
    try { await api.delete(`/email/lists/${l.id}`); toast.success('List deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <EmailSectionLayout
      actions={
        <Button onClick={() => { setForm(EMPTY); setEditing({}); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-list-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New list
        </Button>
      }
    >
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : lists.length === 0 ? (
        <Card data-testid="lists-empty"><CardContent className="py-14 text-center text-zinc-500">
          <ListChecks className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="font-medium text-zinc-700">No lists yet</p>
          <p className="text-sm">Create a list to group subscribers for a targeted send.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lists-grid">
          {lists.map((l) => (
            <Card key={l.id} className="group" data-testid={`list-card-${l.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center"><ListChecks className="w-4 h-4" /></div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setForm({ id: l.id, name: l.name, description: l.description || '' }); setEditing({}); }} className="p-1.5 text-zinc-400 hover:text-indigo-600" data-testid={`edit-list-${l.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(l)} className="p-1.5 text-zinc-400 hover:text-red-600" data-testid={`delete-list-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-zinc-900">{l.name}</div>
                  {l.description && <div className="text-sm text-zinc-500 line-clamp-2">{l.description}</div>}
                </div>
                <button onClick={() => navigate(`/email/subscribers?list=${l.id}`)} className="text-sm text-indigo-600 hover:underline flex items-center gap-1" data-testid={`view-members-${l.id}`}>
                  <Users className="w-3.5 h-3.5" /> {l.subscriber_count} subscriber{l.subscriber_count === 1 ? '' : 's'}
                </button>
                <button
                  onClick={() => openAuto(l)}
                  data-testid={`automate-list-${l.id}`}
                  className={`w-full flex items-center gap-1.5 text-xs rounded-md px-2 py-1.5 border transition-colors ${labelFor(l)
                    ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                    : 'border-dashed border-zinc-200 text-zinc-400 hover:text-indigo-600 hover:border-indigo-200'}`}
                >
                  <Zap className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{labelFor(l) || 'Add automation on join'}</span>
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent data-testid="list-dialog">
          <DialogHeader><DialogTitle>{form.id ? 'Edit list' : 'New list'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ln">Name</Label>
              <Input id="ln" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="VIP Customers" data-testid="list-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ld">Description</Label>
              <Textarea id="ld" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" data-testid="list-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setForm(EMPTY); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-list-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{form.id ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!autoList} onOpenChange={(o) => { if (!o) setAutoList(null); }}>
        <DialogContent data-testid="automation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Automation for “{autoList?.name}”</DialogTitle>
            <DialogDescription>
              When a new subscriber joins this list (e.g. from a funnel page or import), automatically run this action — hands-free.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Action</Label>
              <Select value={autoForm.action} onValueChange={(v) => setAutoForm({ ...autoForm, action: v, target_id: '' })}>
                <SelectTrigger data-testid="automation-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enroll_sequence">Enroll in a sequence (drip)</SelectItem>
                  <SelectItem value="send_campaign">Send a one-off email (broadcast)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{autoForm.action === 'send_campaign' ? 'Broadcast to send' : 'Sequence to enroll in'}</Label>
              <Select value={autoForm.target_id || ''} onValueChange={(v) => setAutoForm({ ...autoForm, target_id: v })}>
                <SelectTrigger data-testid="automation-target-select">
                  <SelectValue placeholder={autoForm.action === 'send_campaign' ? 'Pick a broadcast' : 'Pick a sequence'} />
                </SelectTrigger>
                <SelectContent>
                  {(autoForm.action === 'send_campaign' ? broadcasts : sequences).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(autoForm.action === 'send_campaign' ? broadcasts : sequences).length === 0 && (
                <p className="text-xs text-amber-600">
                  No eligible {autoForm.action === 'send_campaign' ? 'broadcasts (need subject + body)' : 'sequences (need at least one step)'} yet — create one in Campaigns first.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Active</Label>
              <Switch checked={autoForm.active} onCheckedChange={(v) => setAutoForm({ ...autoForm, active: v })} data-testid="automation-active-toggle" />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            {autoList?.automation?.target_id ? (
              <Button variant="ghost" onClick={clearAuto} disabled={savingAuto} className="text-red-500" data-testid="remove-automation-btn">Remove</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setAutoList(null)}>Cancel</Button>
              <Button onClick={saveAuto} disabled={savingAuto} className="bg-amber-500 hover:bg-amber-600" data-testid="save-automation-btn">
                {savingAuto ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save automation'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EmailSectionLayout>
  );
};

export default EmailListsPage;
